import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// Open Positions — live state of every open GBEMI TRADE as the exit
// manager sees it. Refreshes every 30s. The page is built for TRADERS so its
// primary visual is "engine is managing — don't close manually". Every card
// makes the upcoming exit conditions explicit (price level, distance from
// current price, whether v1.9 is holding the position past GBEMI's exit).

const REFRESH_MS = 30000;

const TRAIL_COLORS = {
  no_trail_yet: '#6b7280',  // grey — only hard stop active
  pre_trail:    '#f59e0b',  // amber — partial lock-in
  run_trail:    '#3b82f6',  // blue  — full trail engaged
  ext_trail:    '#10b981',  // emerald — let-it-run trail
};

const TRAIL_LABELS = {
  no_trail_yet: 'No trail yet · only hard stop active',
  pre_trail:    'Pre-trail · locks 50% of peak on giveback',
  run_trail:    'Run-trail · exits at peak − 12 bp',
  ext_trail:    'Ext-trail · letting it run, exits at peak − 50 bp',
};

const TRAIL_PLAIN = {
  no_trail_yet: 'Peak below +15 bp · only hard stop −60 bp is armed',
  pre_trail:    'Peak between +15 and +25 bp · if close drops to half of peak, engine locks in half-peak',
  run_trail:    'Peak ≥ +25 bp and price BELOW EMA20 (trend cooling) · engine exits if close gives back 12 bp from peak',
  ext_trail:    'Peak ≥ +25 bp and price ABOVE EMA20 (trend intact) · engine lets it run, exits only if close gives back 50 bp from peak',
};

const V19_PURPLE = '#a855f7';
const V19_PURPLE_BG = 'rgba(168,85,247,0.10)';

function fmtPrice(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtBp(v, withSign = true) {
  if (v == null) return '—';
  const s = withSign && v > 0 ? '+' : '';
  return `${s}${Number(v).toFixed(1)} bp`;
}

function fmtDollars(v) {
  if (v == null) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+$' : n < 0 ? '−$' : '$';
  return `${sign}${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time} ET`;
  } catch { return iso; }
}

function pnlColor(bp) {
  if (bp == null) return T.textMuted;
  if (bp > 0)  return T.bull;
  if (bp < 0)  return T.bear;
  return T.textMuted;
}

function StatBlock({ label, value, sub, accent }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: T.textMuted, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: accent || T.text,
                    fontFamily: 'Roboto Mono, monospace', lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1,
                       fontFamily: 'Roboto Mono, monospace' }}>{sub}</div>
      )}
    </div>
  );
}

function TrailPill({ mode }) {
  const c = TRAIL_COLORS[mode] || T.textMuted;
  const label = TRAIL_LABELS[mode] || mode;
  return (
    <span style={{ padding: '3px 8px', borderRadius: 4,
                   border: `1px solid ${c}`, color: c,
                   fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>
      {label}
    </span>
  );
}

function SideBadge({ side }) {
  const isLong = side === 'long';
  const c = isLong ? T.bull : T.bear;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 4,
                   border: `1px solid ${c}`, color: c,
                   fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                   textTransform: 'uppercase' }}>
      {isLong ? 'LONG' : 'SHORT'}
    </span>
  );
}

// "EXIT CONDITIONS" box — plain-English explanation of what the engine is
// watching for on THIS position. The whole point of this page.
function ExitConditions({ p, params }) {
  const last = p.last_close;
  const side = p.side;
  const sideMul = side === 'long' ? 1 : -1;

  const trailDistBp =
    p.current_stop_price != null && last != null
      ? (sideMul * (last - p.current_stop_price)) / p.entry_price * 10000
      : null;
  const hardDistBp =
    p.hard_stop_price != null && last != null
      ? (sideMul * (last - p.hard_stop_price)) / p.entry_price * 10000
      : null;

  const Row = ({ label, level, price, distBp, breached, accent, note }) => (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10,
      padding: '8px 10px', borderTop: `1px solid ${T.border}`,
      alignItems: 'center', fontFamily: 'Roboto Mono, monospace', fontSize: 11,
    }}>
      <div style={{ color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
        {label}
      </div>
      <div style={{ color: accent || T.text, fontWeight: 700 }}>
        {level}
      </div>
      <div style={{ color: T.text }}>
        {price != null ? fmtPrice(price) : '—'}
      </div>
      <div style={{ color: breached ? T.bear : T.textDim, fontSize: 10 }}>
        {distBp != null
          ? (breached
              ? '⚠ TRIGGER HIT — engine will exit on next tick'
              : `${distBp > 0 ? '+' : ''}${distBp.toFixed(1)} bp of room · ${note || ''}`)
          : (note || '—')}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 6,
                    letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        What the engine is watching for
      </div>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10,
          padding: '6px 10px', background: T.surfaceHi,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMuted,
        }}>
          <div>Branch</div><div>Level (bp)</div><div>Trigger price</div><div>Distance / note</div>
        </div>
        <Row
          label="Hard stop"
          level={`${p.hard_stop_bp != null
                    ? p.hard_stop_bp
                    : (side === 'short' ? params.stop_bp_short : params.stop_bp_long ?? params.stop_bp)} bp from entry`}
          price={p.hard_stop_price}
          distBp={hardDistBp}
          breached={hardDistBp != null && hardDistBp <= 0}
          accent={T.bear}
          note={side === 'short' ? 'short stop (v1.10 widened to −70)' : 'long stop'}
        />
        <Row
          label={p.trail_mode || 'trail'}
          level={p.current_stop_bp != null ? `${p.current_stop_bp.toFixed(1)} bp` : '—'}
          price={p.current_stop_price}
          distBp={trailDistBp}
          breached={trailDistBp != null && trailDistBp <= 0}
          accent={TRAIL_COLORS[p.trail_mode] || T.text}
          note={p.trail_mode === 'no_trail_yet'
            ? 'no trail armed — peak still below +15 bp'
            : 'trail trigger — engine exits if close ≤ this'}
        />
        {p.minutes_until_eod != null && (
          <Row
            label="EOD"
            level={`${params.rth_end_hour_et}:00 ET`}
            price={null}
            distBp={null}
            breached={false}
            note={`${p.minutes_until_eod} min remaining in RTH`}
          />
        )}
        {p.bars_until_time_stop > 0 && (
          <Row
            label="Time stop"
            level={`${params.time_stop_bars} × 3-min`}
            price={null}
            distBp={null}
            breached={false}
            note={`${p.bars_until_time_stop} bars (~${p.bars_until_time_stop * 3} min) remaining`}
          />
        )}
      </div>
      <div style={{ fontSize: 10, color: T.textDim, marginTop: 6, lineHeight: 1.5 }}>
        {TRAIL_PLAIN[p.trail_mode] || ''}
      </div>
    </div>
  );
}

// v1.9 EXTENSION block — shows when this position is being HELD past GBEMI's
// exit. Replaces the standard exit conditions box with the v1.9 DD-stop view.
function V19ExtensionPanel({ p }) {
  return (
    <div style={{
      marginTop: 12, padding: 10, borderRadius: 6,
      background: V19_PURPLE_BG, border: `1px solid ${V19_PURPLE}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: V19_PURPLE,
        }}>
          ● v1.9 EXTENSION ACTIVE
        </span>
        <span style={{ fontSize: 11, color: T.textDim }}>
          GBEMI would have exited at <strong style={{ color: T.text, fontFamily: 'Roboto Mono, monospace' }}>
            {fmtPrice(p.v19_gbemi_exit_price)}
          </strong>
          {' '}(+{p.v19_gbemi_pnl_bp?.toFixed(1)} bp via <strong style={{ color: T.text }}>{p.v19_gbemi_reason}</strong>).
          Engine is HOLDING past that point.
        </span>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10,
        fontFamily: 'Roboto Mono, monospace', fontSize: 11, padding: '8px 0',
        borderTop: `1px solid ${V19_PURPLE}66`,
      }}>
        <div style={{ color: V19_PURPLE, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10, fontWeight: 700 }}>
          v1.9 DD stop
        </div>
        <div style={{ color: V19_PURPLE, fontWeight: 700 }}>
          xp − 60 bp
        </div>
        <div style={{ color: T.text, fontWeight: 700 }}>
          {fmtPrice(p.v19_dd_stop_price)}
        </div>
        <div style={{ color: p.v19_dd_distance_bp <= 0 ? T.bear : T.textDim, fontSize: 10 }}>
          {p.v19_dd_distance_bp != null
            ? (p.v19_dd_distance_bp <= 0
                ? '⚠ TRIGGER HIT — engine will close on next tick'
                : `${p.v19_dd_distance_bp.toFixed(1)} bp of room · exits here if close pierces`)
            : '—'}
        </div>
      </div>
      <div style={{ fontSize: 10, color: T.textDim, marginTop: 6, lineHeight: 1.5 }}>
        Position is in EOD-hold: engine watches for price to either (a) pierce the v1.9 DD stop above,
        or (b) reach 16:00 ET, whichever comes first. Standard GBEMI trail no longer applies.
      </div>
    </div>
  );
}

function PositionCard({ p, params }) {
  const accent = p.v19_extension_active ? V19_PURPLE : (TRAIL_COLORS[p.trail_mode] || T.textMuted);

  return (
    <div style={{ padding: '16px 18px', borderRadius: 8, marginBottom: 14,
                  background: T.bg, border: `1px solid ${T.border}`,
                  borderLeft: `3px solid ${accent}` }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12,
                    marginBottom: 14, flexWrap: 'wrap' }}>
        <SideBadge side={p.side} />
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
          {p.book || '(no book)'}
        </span>
        <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
          {p.signal_id}
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {p.v19_extension_active
            ? <span style={{
                padding: '3px 8px', borderRadius: 4,
                border: `1px solid ${V19_PURPLE}`, color: V19_PURPLE,
                fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
              }}>v1.9 EXTENSION · DD stop only</span>
            : <TrailPill mode={p.trail_mode} />}
        </span>
      </div>

      {/* Stat row */}
      <div style={{ display: 'flex', gap: 18, marginBottom: 4, flexWrap: 'wrap' }}>
        <StatBlock label="Entry"   value={fmtPrice(p.entry_price)}
                                    sub={fmtTime(p.entry_time_utc)} />
        <StatBlock label="Last"    value={fmtPrice(p.last_close)}
                                    sub={p.last_bar_time ? fmtTime(p.last_bar_time) : '—'} />
        <StatBlock label="PnL bp"  value={fmtBp(p.current_pnl_bp)}
                                    accent={pnlColor(p.current_pnl_bp)} />
        <StatBlock label="PnL $"   value={fmtDollars(p.current_pnl_dollars)}
                                    accent={pnlColor(p.current_pnl_dollars)}
                                    sub="(1c × $20/pt)" />
        <StatBlock label="Peak"    value={fmtBp(p.peak_bp, false)}
                                    sub={p.peak_price != null ? `@ ${fmtPrice(p.peak_price)}` : '—'} />
        <StatBlock label="Bars held" value={`${p.bars_held} / ${params.time_stop_bars}`}
                                      sub={`${p.bars_until_time_stop} bars to time-stop`} />
        <StatBlock label="EOD in"  value={p.minutes_until_eod != null
                                          ? `${p.minutes_until_eod} min`
                                          : '—'} />
      </div>

      {p.v19_extension_active
        ? <V19ExtensionPanel p={p} />
        : <ExitConditions p={p} params={params} />}
    </div>
  );
}

// Big trader-facing banner at the top of the page. The whole point of this
// redesign — make it impossible to miss that the engine is on the position.
function DoNotCloseBanner({ totalPositions, anyExtension }) {
  if (totalPositions === 0) return null;
  return (
    <div style={{
      padding: '14px 18px', marginBottom: 16, borderRadius: 8,
      background: 'rgba(8,153,129,0.10)',
      border: `2px solid ${T.bull}`,
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <span style={{
        fontSize: 16, fontWeight: 800, letterSpacing: '0.05em', color: T.bull,
        whiteSpace: 'normal',
      }}>
        🔒 ENGINE IS MANAGING — DO NOT CLOSE MANUALLY
      </span>
      <span style={{ fontSize: 11, color: T.textDim, lineHeight: 1.5, flex: 1, minWidth: 'min(100%, 280px)' }}>
        {totalPositions} live position{totalPositions === 1 ? '' : 's'} below. The exit manager runs every minute
        on fresh 3-min bars and will close each position automatically when its trail / hard-stop / EOD / time-stop
        triggers fire. Manual closes deviate from the cert and reduce expected P&L.
        {anyExtension && (
          <span style={{ display: 'block', marginTop: 4, color: V19_PURPLE, fontWeight: 700 }}>
            v1.9 EXTENSION ACTIVE on at least one position — the position is being HELD past GBEMI's
            would-be exit per the v1.9 cert. This is normal; the position closes on DD stop or EOD.
          </span>
        )}
      </span>
    </div>
  );
}

// One "strategy section" — header + position cards (or empty state).
function StrategySection({ title, color, data, error }) {
  const positions = data?.positions || [];
  const params    = data?.params || {};
  const phaseNote = data?.phase_note;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color, letterSpacing: '0.04em' }}>
          {title}
        </h2>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {positions.length} open · manager: {' '}
          <strong style={{ color: data?.manager_enabled ? T.bull : T.bear }}>
            {data?.manager_enabled ? 'ENABLED' : 'DISABLED'}
          </strong>
          {data?.v19_enabled !== undefined && (
            <span style={{ marginLeft: 8 }}>
              · v1.9: <strong style={{ color: data.v19_enabled ? V19_PURPLE : T.textMuted }}>
                {data.v19_enabled ? 'ON' : 'OFF'}
              </strong>
            </span>
          )}
        </span>
      </div>

      {phaseNote && (
        <div style={{
          padding: '10px 14px', borderRadius: 6,
          background: 'rgba(168,85,247,0.06)', border: `1px dashed ${V19_PURPLE}88`,
          fontSize: 11, color: T.textDim, marginBottom: 10,
        }}>
          {phaseNote}
        </div>
      )}

      {error && (
        <div style={{ padding: 12, borderRadius: 6,
                       background: 'rgba(242,54,69,0.08)',
                       border: `1px solid rgba(242,54,69,0.3)`,
                       color: T.bear, fontSize: 12, marginBottom: 10 }}>
          Failed to load: {error}
        </div>
      )}

      {/* Parameter strip — only for strategies with active params */}
      {params.stop_bp != null && (
        <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 10,
                       background: T.bg, border: `1px solid ${T.border}`,
                       display: 'flex', gap: 14, flexWrap: 'wrap',
                       fontFamily: 'Roboto Mono, monospace', fontSize: 10,
                       color: T.textDim }}>
          <span>Engine: <strong style={{ color: T.text }}>EXT50</strong></span>
          <span>Hard stop L/S: <strong style={{ color: T.text }}>
            {params.stop_bp_long ?? params.stop_bp}/{params.stop_bp_short ?? params.stop_bp} bp
          </strong></span>
          <span>Activation L/S: <strong style={{ color: T.text }}>{params.activate_long}/{params.activate_short} bp</strong></span>
          <span>Pre-trail ≥ <strong style={{ color: T.text }}>{params.pre_trail_lo} bp</strong></span>
          <span>Trail giveback: <strong style={{ color: T.text }}>{params.gb_base}</strong>/<strong style={{ color: T.text }}>{params.gb_ext} bp</strong></span>
          <span>EOD: <strong style={{ color: T.text }}>{params.rth_end_hour_et}:00 ET</strong></span>
          {params.v19_dd_stop_bp != null && (
            <span>v1.9 DD: <strong style={{ color: V19_PURPLE }}>−{params.v19_dd_stop_bp} bp from xp</strong></span>
          )}
          {data?.v110_short_stop_70_enabled && (
            <span style={{ marginLeft: 4 }}>
              · v1.10: <strong style={{ color: T.bull }}>SHORT stop = −70 bp</strong>
            </span>
          )}
        </div>
      )}

      {positions.length === 0 ? (
        <div style={{ padding: '24px 20px', textAlign: 'center',
                       background: T.bg, border: `1px solid ${T.border}`,
                       borderRadius: 8, color: T.textMuted }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>No open positions.</div>
          <div style={{ fontSize: 11 }}>
            New TRADEs will appear here within ~30 seconds.
          </div>
        </div>
      ) : (
        positions.map((p) => (
          <PositionCard key={p.signal_id || p.entry_time_utc} p={p} params={params} />
        ))
      )}
    </div>
  );
}

export default function OpenPositions() {
  const [gbemi,   setGbemi]   = useState(null);
  const [errGbemi, setErrGbemi]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const fetchOne = async (path, setData, setErr) => {
      try {
        const res = await fetch(`${API_BASE}${path}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!j.ok) throw new Error(j.error || 'unknown');
        setData(j); setErr(null);
      } catch (e) {
        setErr(e.message || String(e));
      }
    };
    await fetchOne('/strategies/api/strategies/gbemi/open-positions/?strategy=GBEMI', setGbemi, setErrGbemi);
    setLastFetched(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData]);

  const gbemiPositions = gbemi?.positions || [];
  const total = gbemiPositions.length;
  const anyExtension = gbemiPositions.some(p => p.v19_extension_active);

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1500, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                    paddingBottom: 14, borderBottom: `1px solid ${T.border}`,
                    marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
          Open Positions
        </h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          GBEMI · refreshes every {REFRESH_MS / 1000}s
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center',
                      flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 280px' }}>
          {lastFetched && (
            <span style={{ fontSize: 11, color: T.textMuted,
                            fontFamily: 'Roboto Mono, monospace' }}>
              updated {lastFetched.toLocaleTimeString('en-GB',
                       { hour12: false, timeZone: 'America/New_York' })} ET
            </span>
          )}
          <button onClick={fetchData} disabled={loading}
                  style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
                           letterSpacing: '0.07em', textTransform: 'uppercase',
                           borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
                           border: 'none', background: 'rgba(245,158,11,0.18)',
                           color: '#f59e0b' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Big "do not close" banner — only when there are positions */}
      <DoNotCloseBanner totalPositions={total} anyExtension={anyExtension} />

      <StrategySection title="GBEMI"  color="#10b981" data={gbemi}  error={errGbemi} />

      {/* Always-visible footer note about why this page exists */}
      <div style={{
        marginTop: 20, padding: '12px 14px', borderRadius: 6,
        background: T.surfaceHi, border: `1px dashed ${T.border}`,
        fontSize: 11, color: T.textDim, lineHeight: 1.6,
      }}>
        <strong style={{ color: T.text }}>Why this page exists:</strong>{' '}
        The exit manager runs every minute on the same 3-min bars the cert was
        backtested on. It will fire a TRADE → EXIT row automatically when any
        of the engine's branches trigger. Closing a position manually before
        the engine fires deviates from the cert and reduces expected P&L —
        sometimes by a lot. If you ever feel like "this trade should be closed",
        check this page first: every open position has its exit conditions
        spelled out below. If the engine looks broken or the trail isn't
        updating, ping engineering; don't override.
      </div>

    </div>
    </div>
  );
}
